import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Radio,
  RadioGroup,
  Collapse,
  Grid,
  FormGroup,
  FormControl,
  FormLabel,
  CircularProgress,
  FormControlLabel,
  MenuItem
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { TopNavBar } from 'components/top_nav_bar';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';
import { NumericFormat } from 'react-number-format';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { debounce } from 'lodash';
import { getAllowedUnits } from 'utils/forms/cuffa_functions';
import ProdutoDetalhes, { ProdutoItem } from '../../components/ProdutoDetalhes';
import { mapApiProductToProdutoSelecionado, mergeSavedConventionalProduct } from './helpers';
import {
  parseAvailabilityMonths,
  safeParseFloat,
  parseApiProductToProduto,
  validateProdutos,
  buildConventionalApiData,
} from '../helpers';
import SearchableList from 'components/shared/SearchableList';
import { ConventionalProduct, ConventionalProductionData, Product, ProdutoSelecionado } from '../../types';

// (Removido) Mapeamentos de meses não utilizados

// ProdutoDetalhes and ProdutoItem moved to shared component to remove duplication

export default function ProducaoConvencional() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { salvarDadosParciais, dadosParciais } = useCuffaStore();
  const [practicesConventional, setPracticesConventional] = useState<boolean>(false);
  const [produtos, setProdutos] = useState<ProdutoSelecionado[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [loading, setLoading] = useState(false);

  // Função para carregar dados da API
  const carregarDadosDaAPI = useCallback(async () => {
    if (!uuid) return;

    try {
      const response = await CuffaAPI.getDataByID(uuid);

      if (response && response.dados) {
        const apiData = response.dados;

        // Definir se pratica produção convencional
        setPracticesConventional(apiData.family_practices_conventional_production ?? false);

        // Carregar produtos da API
        if (apiData.family_practices_conventional_production && apiData.products) {
          const produtosDaAPI = apiData.products
            .filter((p: any) => p.management_type === 'CONVENCIONAL')
            .map((p: any) => parseApiProductToProduto(p, getAllowedUnits as any));

          setProdutos(prev => {
            const produtosBase = prev.filter(p => !p.selecionado);
            return [...produtosBase, ...produtosDaAPI];
          });
        }
      }
    } catch (error) {
      console.error("Erro ao carregar dados da API:", error);
      setSnackbar({ open: true, message: 'Erro ao carregar dados salvos.', severity: 'error' });
    }
  }, [uuid]);

  // Buscar produtos disponíveis e carregar dados
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await CuffaAPI.getProducts(['VEGETAL', 'ANIMAL']);

        const produtosFormatados: ProdutoSelecionado[] = response.dados.map((produto: Product) => mapApiProductToProdutoSelecionado(produto));

        setProdutos(produtosFormatados);

        // Verificar se há dados salvos localmente primeiro
        if (dadosParciais.producaoConvencional && produtosFormatados.length > 0) {
          setPracticesConventional(dadosParciais.producaoConvencional.practices_conventional_production);

          setProdutos(prev => prev.map(produto => {
            const savedProduct = dadosParciais.producaoConvencional?.conventional_products?.find(
              (sp: ConventionalProduct) => sp.product_id === produto.uuid
            );
            return mergeSavedConventionalProduct(produto, savedProduct);
          }));
        } else {
          // Se não há dados locais, carregar da API
          await carregarDadosDaAPI();
        }
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar produtos disponíveis.',
          severity: 'error'
        });
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();

    // Cleanup do debounce
    return () => {
      debouncedHandleProdutoChange.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosParciais, carregarDadosDaAPI]);

  const validate = () => {
    const res = validateProdutos(practicesConventional, produtos as any);
    if (!res.valid) {
      setSnackbar({ open: true, message: res.message || 'Validação falhou.', severity: 'error' });
      return false;
    }
    return true;
  };

  const handleSave = async (navigateNext = false) => {
    if (!validate() || !uuid) return;
    try {
      setLoading(true);
      const apiData: ConventionalProductionData = buildConventionalApiData(uuid, practicesConventional, produtos as any);
      await CuffaAPI.updateConventionalProduction(uuid, apiData);
      // Salvar no storage local incluindo o nome do produto em cada produto
      salvarDadosParciais({
        producaoConvencional: apiData,
        ultimaRota: location.pathname,
      });
      setSnackbar({ open: true, message: 'Dados salvos com sucesso!', severity: 'success' });
      if (navigateNext) {
        setTimeout(() => navigate(ROUTES.PRODUTOS_PROCESSADOS_INCLUIR.replace(':uuid', uuid)), 1000);
      }
    } catch (error) {
      console.error('Erro ao salvar produção convencional:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar os dados. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarProduto = useCallback((uuid: string) => {
    setProdutos(prev => prev.map(p =>
      p.uuid === uuid
        ? { ...p, selecionado: !p.selecionado, expandido: !p.selecionado }
        : p
    ));
  }, []);

  const handleExpandirProduto = useCallback((uuid: string) => {
    setProdutos(prev => prev.map(p =>
      p.uuid === uuid ? { ...p, expandido: !p.expandido } : p
    ));
  }, []);

  // Debounced handler para mudanças nos produtos - OTIMIZADO
  const debouncedHandleProdutoChange = useMemo(
    () => debounce((uuid: string, campo: string, valor: any) => {
      setProdutos(prev => prev.map(p => {
        if (p.uuid !== uuid) return p;

        if (campo.startsWith('availability_months.')) {
          const subCampo = campo.split('.')[1];
          return {
            ...p,
            availability_months: {
              ...p.availability_months,
              [subCampo]: valor
            }
          };
        }
        return { ...p, [campo]: valor };
      }));
    }, 300),
    []
  );

  if (loadingProducts) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produção Convencional" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography ml={2}>Carregando produtos...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Produção Convencional" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Produção Convencional" width="100%">
            <Box mb={4}>
              <Typography fontWeight="bold" gutterBottom>
                A unidade familiar pratica produção convencional? *
              </Typography>
              <RadioGroup
                row
                value={practicesConventional}
                onChange={(e) => {
                  const value = e.target.value === 'true';
                  setPracticesConventional(value);
                  if (!value) {
                    setProdutos(prev => prev.map(p => ({
                      ...p,
                      selecionado: false,
                      expandido: false
                    })));
                  }
                }}
              >
                <FormControlLabel value={true} control={<Radio />} label="Sim" />
                <FormControlLabel value={false} control={<Radio />} label="Não" />
              </RadioGroup>
            </Box>
            {practicesConventional && (
              <>
                <Typography variant="h6" gutterBottom>
                  Produtos Convencionais Produzidos
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Selecione os itens produzidos no modo convencional:
                </Typography>

                <SearchableList
                  items={produtos}
                  getLabel={(p) => p.name}
                  placeholder="Buscar produto..."
                  listSx={{
                    maxHeight: '500px',
                    mb: 3,
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    overflow: 'auto',
                    p: 1
                  }}
                >
                  {(produto: ProdutoSelecionado) => (
                    <ProdutoItem
                      key={produto.uuid}
                      produto={produto}
                      onSelecionar={handleSelecionarProduto}
                      onExpandir={handleExpandirProduto}
                      onChange={debouncedHandleProdutoChange}
                    />
                  )}
                </SearchableList>
              </>
            )}
            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.PRODUCAO_AGROECOLOGICA_INCLUIR.replace(':uuid', uuid ?? ''))}
                disabled={loading}
              >
                Retornar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleSave(false)}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSave(true)}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Avançar'}
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
}

// Exported helper: map an API `Product` to the local `ProdutoSelecionado` shape
// helpers moved to ./helpers.ts

export { mapApiProductToProdutoSelecionado, mergeSavedConventionalProduct } from './helpers';
export { ProdutoDetalhes, ProdutoItem } from '../../components/ProdutoDetalhes';
